import { CSSProperties, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

export const defaultTestIdFullDoc = 'secure-iframe-full-doc';

const Iframe = styled.iframe<{ frameHeight: number | string }>`
  border: none;
  display: flex; /** gets rid of weird space at the bottom */
  height: ${({ frameHeight, theme }) =>
    typeof frameHeight === 'string' ? frameHeight : theme.spacing.utils.pxToRem(frameHeight)};
`;

type IframeFullDocProps = {
  children: string;
  className?: string; // allows styling of iframe via styled-components
  height?: number | string; // see note below
  style?: CSSProperties;
  testId?: string;
  isMaxHeightPref?: boolean;
};

/*
 * Secure IframeFullDoc
 * WARNING: this iframe will reload each time the content changes, so this is NOT a good option for
 *          content that changes frequently (preview of side-by-side editor) as it will scroll to
 *          the top on each update and may cause some noticeable flicker of the content (images)
 *
 * This component simplifies the wrapping of a potentially dangerous html document (containing html
 * head, and body tags) with an iframe.
 *
 * This component is suitable for passing a child as stringified html. Example:
 *   <IframeFullDoc>
 *     {removeScriptFullDoc(content.html, cspTag)}
 *   </IframeFullDoc>
 *
 * Note: In the interest of being thorough, it is recommended that you first sanitize the HTML
 *       content with the removeScriptFullDoc() utility from 'utils/script'. This utility also takes
 *       second (optional) parameter for a CSP that is internal to the iframe. A preconfigured CSP
 *       is available as cspTag from the constants folder in this module
 *
 * Security considerations:
 * - the iframe itself is sandboxed ('allow-same-origin' is necessary for React to manipulate the
 *   contents in real time)
 * - a CSP (if provided) is injected into the header of the iframe's content, allowing only the
 *   resources that are absolutely necessary for our members content to render correctly
 * - the html document used for the source of the iframe is sanitized with removeScriptFullDoc()
 *   prior to rendering
 *
 * Styling:
 * This component may be styled normally with styled-components
 *
 * Height:
 * The height prop allows the consumer to specify the height of the iframe with these rules
 * - a string value will win over any calculation
 * - a number value > 0 will set the min-height, but the height will fall back to 100% (to fill the container)
 * - 0 (or undefined) will set the min-height of the iframe to a calculated value based on its contents (with a height of 100% to be sure to fill the container)
 */
export const IframeFullDoc = ({
  children,
  className,
  height = 0,
  style,
  testId = defaultTestIdFullDoc,
  isMaxHeightPref = false,
}: IframeFullDocProps) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [calculatedHeight, setCalculatedHeight] = useState<number | string>(0);
  const [lastRenderEpoch, setLastRenderEpoch] = useState<number>(0);

  const onLoad = () => {
    setCalculatedHeight(0); // reset the calculatedHeight to its initial value, so the next re-render is accurate
    setLastRenderEpoch(Date.now()); // leverage lifecycle to trigger the remeasure after calculatedHeight has been reset
  };

  useEffect(() => {
    if (height === 0) {
      // indicates the intent to measure the contents
      const { body, documentElement } = iframeRef.current?.contentWindow?.document || {
        body: {},
        documentElement: {},
      };
      // @ts-ignore
      const { offsetHeight: bodyOffsetHeight = 0, scrollHeight: bodyScrollHeight = 0 } = body || {};
      // @ts-ignore
      const { offsetHeight: htmlOffsetHeight = 0, scrollHeight: htmlScrollHeight = 0 } =
        documentElement || {};

      const maxHeight = Math.max(
        bodyOffsetHeight,
        bodyScrollHeight,
        htmlOffsetHeight,
        htmlScrollHeight,
      );
      /**
       *  In most edge cases where height isn't as expected is due to some issues with iframe height calculation from above
       *  In these cases we want to convert the height to 100% as default so to do this while still rendering
       *  most template previews as expected, we determine if there is a condition and if so we use the 100% height
       */

      if (isMaxHeightPref) {
        setCalculatedHeight(maxHeight);
      } else {
        setCalculatedHeight(bodyOffsetHeight === 0 ? '100%' : maxHeight);
      }
    }
  }, [height, lastRenderEpoch, isMaxHeightPref]);

  useEffect(() => {
    onLoad();
  }, [height, style, className]);

  /* note: alternatively, we could implement src={`data:text/html,${children}`}, but it would
   * violate the sandbox 'allow-same-origin' setting (this would be considered cross-origin) */

  return (
    <Iframe
      className={className}
      data-test={testId}
      frameHeight={height || calculatedHeight} // use 'frameHeight' instead of 'height' to avoid the legacy height attribute on the iframe
      onLoad={onLoad}
      ref={iframeRef}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" // allow-same-origin: allows us to access the iframe's innards via JS; allow-popups: Allows new windows/tabs to be opened from links; allow-popups-to-escape-sandbox: allows a sandboxed document to open new windows without forcing the sandboxing flags upon them
      srcDoc={children}
      style={style}
      tabIndex={-1} // don't want content inside the iframe to be focusable
    />
  );
};

export default IframeFullDoc;
